/**
 * Resolves CSS variables to their actual computed values
 * @param cssValue - The CSS value that might contain variables (e.g., "var(--main-color)")
 * @returns The resolved color value (e.g., "#eb15e8")
 */
export const resolveCSSVariable = (cssValue: string): string => {
  // If it's not a CSS variable, return as-is
  if (!cssValue.startsWith("var(")) {
    return cssValue;
  }

  // Try to read the raw CSS variable value from :root first to preserve alpha
  try {
    const match = cssValue.match(/var\((--[^,\s)]+)\s*(?:,[^)]+)?\)/);
    if (match) {
      const varName = match[1];
      const rootValue = window.getComputedStyle(document.documentElement).getPropertyValue(varName);
      const trimmed = rootValue.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  } catch (_error) {
    // Fall through to computed color resolution
  }

  // Create a temporary element to compute the CSS variable
  const tempElement = document.createElement("div");
  tempElement.style.color = cssValue;
  tempElement.style.visibility = "hidden";
  tempElement.style.position = "absolute";
  tempElement.style.top = "-9999px";

  // Add to DOM temporarily to compute the value
  document.body.appendChild(tempElement);

  try {
    // Get the computed color value
    const computedValue = window.getComputedStyle(tempElement).color;

    // Remove the temporary element
    document.body.removeChild(tempElement);

    // If we got a valid color, return it
    if (computedValue && computedValue !== "rgb(0, 0, 0)" && computedValue !== "rgba(0, 0, 0, 0)") {
      return computedValue;
    }

    // Fallback: try to get the computed value directly
    const fallbackElement = document.createElement("div");
    fallbackElement.style.setProperty("--temp-var", cssValue);
    fallbackElement.style.color = "var(--temp-var)";
    document.body.appendChild(fallbackElement);

    const fallbackValue = window.getComputedStyle(fallbackElement).color;
    document.body.removeChild(fallbackElement);

    return fallbackValue || cssValue;
  } catch (error) {
    // Clean up on error
    try {
      document.body.removeChild(tempElement);
    } catch (_cleanupError) {
      // Ignore cleanup errors
    }

    console.warn("Failed to resolve CSS variable:", cssValue, error);
    return cssValue; // Return original value as fallback
  }
};

/**
 * Resolves multiple CSS variables in a string
 * @param cssString - String that might contain multiple CSS variables
 * @returns String with all CSS variables resolved
 */
export const resolveCSSVariables = (cssString: string): string => {
  // Find all var() references in the string
  const varRegex = /var\([^)]+\)/g;
  const matches = cssString.match(varRegex);

  if (!matches) {
    return cssString;
  }

  let resolvedString = cssString;

  // Replace each var() with its resolved value
  matches.forEach((varRef) => {
    const resolvedValue = resolveCSSVariable(varRef);
    resolvedString = resolvedString.replace(varRef, resolvedValue);
  });

  return resolvedString;
};
